import { FC, KeyboardEvent, SyntheticEvent, memo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import __ from "web/Layout/Translations";

import urls from "web/constants/urls";

import { PropsWithClasses } from "web/types/Common";
import { Nullable } from "web/types/Utils";

import classify from "web/classify";
import { closeNavigation } from "web/features/app/appSlice";

import type { NavigationItem } from "../navigationPanel";
import Points from "./Points";
import defaultClasses from "./panelItem.scss";

interface IPanelItemProps {
  item: NavigationItem;
  pathname: string;
  pageType: string;
  isNavigationOpened: boolean;
}

const PanelItem: FC<PropsWithClasses<IPanelItemProps>> = memo(
  ({ classes = {}, item, pathname = "", isNavigationOpened }) => {
    const dispatch = useDispatch();
    const { name, link, icon: Icon } = item;
    const isActive =
      !isNavigationOpened &&
      // 0. Default behaviour
      (pathname === link ||
        // Behaviour for all "/customer" tabs
        // 1. Must start with "/customer"
        (link.indexOf(urls.customer) === 0 &&
          pathname.indexOf(urls.customer) === 0 &&
          // 2. Can't be "/customer/wishlist" cause wishlist would be checked while being on "/customer"/
          // "/customer/wishlist" is handled by default behaviour
          link !== urls.customerWishlist &&
          // 3. Behaviour from 2 can apply only while being on "/customer/something"
          pathname !== urls.home &&
          pathname !== urls.productsQuickList &&
          pathname !== urls.customerWishlist &&
          pathname !== urls.contactForm) ||
        // 4. Spacial behaviour for help-center
        (link === urls.customer &&
          (pathname.indexOf(urls.helpCenter) === 0 ||
            pathname.indexOf(urls.contactForm) === 0)));
  
    const isCustomer = typeof pathname === "string" && pathname === urls.customer;
    const classNameRoot = isActive
      ? `${classes.root} ${classes.rootActive}`
      : classes.root;
    const classNameRootCustomer = isCustomer
      ? `${classNameRoot} ${classes.customer}`
      : classNameRoot;
  
    const content = (
      <span className={classes.iconBox} aria-hidden="true">
        <Icon className={classes.icon} />
      </span>
    );
  
    const history = useHistory();
  
    const isTouchScreen = "ontouchstart" in document.documentElement;
    const touchEnd = isTouchScreen ? "touchend" : "mouseup";
  
    let lastTimer = 0;
    let startTimer = 0;
    let doubleClickFlag = false;
    let clickTarget: Nullable<HTMLElement> = null;
    const DOUBLE_CLICK_TIMEOUT = 250;
  
    function clickTypeDetector(
      event: SyntheticEvent,
      singleClickHandler: Nullable<() => void>,
      doubleClickHandler: Nullable<() => void>,
      longClickHandler: Nullable<() => void>
    ) {
      startTimer = new Date().getTime();
      doubleClickFlag = false;
  
      if (startTimer - lastTimer < DOUBLE_CLICK_TIMEOUT) {
        lastTimer = 0;
        doubleClickFlag = true;
        if (doubleClickHandler) {
          doubleClickHandler();
        }
      }
  
      clickTarget = event.currentTarget as HTMLElement;
  
      clickTarget.addEventListener(touchEnd, function handler() {
        const touchTimer = new Date().getTime();
  
        if (touchTimer - startTimer >= DOUBLE_CLICK_TIMEOUT) {
          if (longClickHandler) {
            longClickHandler();
          }
        } else {
          setTimeout(() => {
            if (!doubleClickFlag) {
              if (singleClickHandler) {
                singleClickHandler();
              }
            }
          }, DOUBLE_CLICK_TIMEOUT);
        }
        clickTarget?.removeEventListener(touchEnd, handler);
      });
  
      lastTimer = new Date().getTime();
      return 0;
    }
  
    const goToLink = () => {
      history.push(link);
    };

    const goToLinkAndCloseNavigation = () => {
      history.push(link);
      dispatch(closeNavigation());
    };

    const singleClickHandler = isActive ? null : goToLinkAndCloseNavigation;
    const repeatClickHandler = isActive ? goToLink : null;

    const interactionHandler = isTouchScreen
      ? {
          onTouchStart: (e: SyntheticEvent) =>
            clickTypeDetector(
              e,
              singleClickHandler,
              repeatClickHandler,
              repeatClickHandler
            ),
        }
      : {
          onMouseDown: (e: SyntheticEvent) =>
            clickTypeDetector(
              e,
              singleClickHandler,
              repeatClickHandler,
              repeatClickHandler
            ),
        };

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
      if (singleClickHandler) {
        singleClickHandler();
      }
    };

    return (
      <div className={classNameRootCustomer}>
        <button
          type="button"
          className={classes.button}
          title={__(name)}
          data-t1="panel_item_link"
          data-t2={name}
          onKeyDown={handleKeyDown}
          {...interactionHandler}
        >
          <span className="visually-hidden">{__(name)}</span>
          {content}
          {!isActive && urls.customer === link && <Points />}
        </button>
      </div>
    );
  });

export default classify<PropsWithClasses<IPanelItemProps>>(defaultClasses)(
  PanelItem
);